import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { api } from '../../../shared/api';
import NumberInput from '../../../components/form/NumberInput';
import { useRealtimeEvents } from '../../../shared/useRealtimeEvents';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ContractCases from './ContractCases';

const STATUS_FULL = 'full';
const STATUS_PARTIAL = 'partial';
const STATUS_NONE = 'none';

function getDeployStatus(planned, deployed) {
  if (deployed >= planned) return STATUS_FULL;
  if (deployed > 0) return STATUS_PARTIAL;
  return STATUS_NONE;
}

function DeployStatusBadge({ planned, deployed, t }) {
  const st = getDeployStatus(planned, deployed);
  const cls = st === STATUS_FULL ? 'success' : st === STATUS_PARTIAL ? 'warning' : 'secondary';
  return (
    <span className={`badge bg-${cls}`}>
      {deployed}/{planned} — {t(`contracts.deploy.status.${st}`)}
    </span>
  );
}

function ShortageBadge({ shortage, t }) {
  if (!shortage) return null;
  return (
    <span className="badge bg-danger ms-1" title={t('contracts.deploy.shortageHint', { count: shortage })}>
      {t('contracts.deploy.shortfall')}: {shortage}
    </span>
  );
}

const ContractDeploy = ({ contractId, items = [] }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [deploying, setDeploying] = useState(false);
  const [message, setMessage] = useState(null);
  const [selected, setSelected] = useState({});

  const { data: deployedUnits = [], isLoading: loadingUnits } = useQuery({
    queryKey: ['contract-deployed-units', contractId],
    queryFn: async () => {
      const res = await api.get(`contracts/api/contracts/${contractId}/deployed_units/`);
      return res.data || res;
    },
  });

  useRealtimeEvents((e) => {
    if (e.model === 'warehouses.itemunit' || e.model === 'contracts.contractitemallocation') {
      queryClient.invalidateQueries({ queryKey: ['contract-deployed-units', contractId] });
      queryClient.invalidateQueries({ queryKey: ['contract-availability', contractId] });
    }
  });

  const internalAllocations = items.flatMap(item => (item.allocations || [])
    .filter(allocation => allocation.source_type === 'INTERNAL' && allocation.stock_item)
    .map(allocation => ({ ...allocation, contract_item_id: item.id, product_name: item.product_name, planned_quantity: allocation.quantity })));
  const stockItemIds = internalAllocations.map(allocation => allocation.stock_item);
  const { data: availableMap = {} } = useQuery({
    queryKey: ['contract-availability', contractId, stockItemIds.join(',')],
    queryFn: async () => {
      const res = await api.get(
        `warehouses/api/item-units/?status=available&stock_item_in=${stockItemIds.join(',')}`
      );
      const units = res.data?.results ?? res.data ?? res;
      const map = {};
      for (const u of units) { map[u.stock_item] = (map[u.stock_item] || 0) + 1; }
      return map;
    },
    enabled: stockItemIds.length > 0,
  });

  const refreshDeploy = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['contract-deployed-units', contractId] });
    queryClient.invalidateQueries({ queryKey: ['contract-availability', contractId] });
  }, [queryClient, contractId]);

  const fetchDeployedUnits = refreshDeploy;
  const fetchAvailability = refreshDeploy;

  // Count deployed units per stock item
  const deployedCountByStockItem = {};
  for (const u of deployedUnits) {
    deployedCountByStockItem[u.stock_item] = (deployedCountByStockItem[u.stock_item] || 0) + 1;
  }

  const handleSelectQty = (stockItemId, value) => {
    setSelected(prev => ({ ...prev, [stockItemId]: parseInt(value, 10) || 0 }));
  };

  const handleDeploy = async (subset) => {
    setDeploying(true);
    setMessage(null);
    try {
      const itemsPayload = subset.map(({ contract_item_id, stock_item_id, quantity }) => ({ contract_item_id, stock_item_id, quantity }));
      const res = await api.post(`contracts/api/contracts/${contractId}/deploy_items/`, {
        items: itemsPayload,
      });
      const data = res.data || res;
      const errors = data.errors || [];
      if (errors.length) {
        setMessage({ type: 'warning', text: errors.map(e => `${e.item_name || e.item_id}: ${e.error}`).join('; ') });
      } else {
        setMessage({ type: 'success', text: t('contracts.deploy.success') });
      }
      refreshDeploy();
    } catch {
      setMessage({ type: 'danger', text: t('contracts.deploy.error') });
    } finally {
      setDeploying(false);
    }
  };

  const handleDeployItem = (allocation) => {
    const qty = selected[allocation.stock_item] ?? Math.max(0, (allocation.planned_quantity || 0) - (deployedCountByStockItem[allocation.stock_item] || 0));
    handleDeploy([{ contract_item_id: allocation.contract_item_id, stock_item_id: allocation.stock_item, quantity: qty }]);
  };

  const handleDeployAll = () => {
    const payload = internalAllocations
      .map(allocation => ({
        contract_item_id: allocation.contract_item_id,
        stock_item_id: allocation.stock_item,
        quantity: allocation.planned_quantity || 1,
      }));
    handleDeploy(payload);
  };

  const deployableItems = internalAllocations;
  const totalShortage = items.reduce((sum, ci) => sum + (ci.shortage_quantity || 0), 0);

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h5 className="mb-0">{t('contracts.deploy.title')}</h5>
          <p className="text-muted small mb-0">{t('contracts.deploy.description')}</p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={handleDeployAll}
          disabled={deploying || !deployableItems.length}
        >
          {deploying ? <Spinner size="sm" className="me-1" /> : <i className="ti ti-truck me-1"></i>}
          {t('contracts.deploy.deployAll')}
        </Button>
      </div>

      {message && (
        <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {totalShortage > 0 && (
        <Alert variant="danger">
          <i className="ti ti-alert-triangle me-1"></i>
          {t('contracts.deploy.shortageWarning', { count: totalShortage })}
        </Alert>
      )}

      {loadingUnits && (
        <div className="text-center py-3"><Spinner size="sm" /></div>
      )}

      {!loadingUnits && !deployableItems.length && (
        <p className="text-muted">{t('contracts.deploy.noItems')}</p>
      )}

      {!loadingUnits && deployableItems.length > 0 && (
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th>{t('contracts.deploy.itemName')}</th>
                <th className="text-center">{t('contracts.deploy.planned')}</th>
                <th className="text-center">{t('contracts.deploy.available')}</th>
                <th className="text-center">{t('contracts.deploy.deployed')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {deployableItems.map((ci) => {
                const deployedCount = deployedCountByStockItem[ci.stock_item] || 0;
                const trackedAvail = availableMap[ci.stock_item];
                const avail = trackedAvail ?? ci.available_quantity ?? '—';
                const defaultQty = Math.max(0, (ci.planned_quantity || 0) - deployedCount);
                const qty = selected[ci.stock_item] ?? defaultQty;

                return (
                  <tr key={ci.id || ci.stock_item}>
                    <td>
                      <strong>{ci.product_name || ci.stock_name || ci.stock_item}</strong>
                      <ShortageBadge shortage={ci.shortage_quantity} t={t} />
                    </td>
                    <td className="text-center">{ci.planned_quantity}</td>
                    <td className="text-center">{avail}</td>
                    <td className="text-center">
                      <DeployStatusBadge planned={ci.quantity} deployed={deployedCount} t={t} />
                    </td>
                    <td className="text-end">
                      <div className="d-flex gap-2 align-items-center justify-content-end">
                        <NumberInput
                          min={0}
                          max={ci.planned_quantity}
                          step="any"
                          value={qty}
                          onChange={(value) => handleSelectQty(ci.stock_item, value)}
                          size="sm"
                          style={{ width: 70 }}
                        />
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleDeployItem(ci)}
                          disabled={deploying || qty === 0}
                        >
                          {t('contracts.deploy.deployBtn')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4">
        <ContractCases contractId={contractId} mode="deploy" />
      </div>
    </div>
  );
};

export default ContractDeploy;
